import { useQuery } from '@tanstack/react-query';
import apiClient from './apiClient';
import { config } from '../config';
import { useAppContext } from '../libs/appContextLib';

/**
 * Hook to fetch user-specific navigation preferences.
 *
 * Fetches from GET /configure/navigation/user-preferences
 *
 * Returns:
 * - pinnedRoutes: Array of route IDs the user has pinned
 * - recentRoutes: Array of recent navigation history entries
 * - navigationPreferences: Object with UI preferences (collapsed categories, etc.)
 *
 * This is separate from company-level route settings (routePreferences)
 * which come from /configure/data/status.
 */
export const useFetchUserPreferences = () => {
  const { currUser } = useAppContext();
  const preferencesUserKey = currUser?.id || currUser?.userId;

  return useQuery({
    queryKey: ['user-preferences', preferencesUserKey],
    queryFn: async () => {
      const { data } = await apiClient.get(
        `${config.REACT_APP_BACKEND_HOST}/configure/navigation/user-preferences`
      );
      return data;
    },
    // Use Cognito user id as soon as it's available to avoid waiting on /configure/data/status
    enabled: !!preferencesUserKey,
    staleTime: 5 * 60 * 1000, // 5 minutes - user preferences don't change frequently
  });
};

export default useFetchUserPreferences;
